import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { cn } from './cn';

/**
 * Campo de formulario: etiqueta, ayuda, error y control, cableados entre sí.
 *
 * the forms de este producto llegaban con tres problemas encadenados
 * (UX_AUDIT H-11/H-12/H-14):
 *
 * 1. La etiqueta iba en versalitas con interletrado ampliado a 11px y gris
 *    claro -- por debajo de AA de contraste, y un 15-20% más ancha que en caja
 *    normal, que es lo que provocaba los truncamientos.
 * 2. Se duplicaba en dos idiomas ("PAÍS / COUNTRY") en una app que ya tiene
 *    i18n, así que se leía dos veces para extraer una idea y ninguno de los dos
 *    idiomas quedaba bien servido.
 * 3. La relación etiqueta-control era visual, no programática: sin `htmlFor` ni
 *    `aria-describedby`, un lector de pantalla anunciaba the field sin nombre y
 *    el mensaje de error no se leía nunca.
 *
 * `Field` Generates los identificadores y los conecta. La etiqueta se pasa ya
 * traducida por quien lo usa (`t('...')`), en un solo idioma.
 */

export interface FieldProps {
  label: string;
  /** Marca the field como obligatorio: asterisco visible y `required` en el control. */
  required?: boolean;
  /** Texto de ayuda permanente, bajo el control. Se enlaza con `aria-describedby`. */
  hint?: string;
  /** Mensaje de error. Sustituye a la ayuda y marca el control como inválido. */
  error?: string;
  /** Acción a la derecha de la etiqueta (p. ej. "¿Olvidaste tu contraseña?"). */
  action?: React.ReactNode;
  className?: string;
  /** Receives los atributos ya cableados; se aplican al control real. */
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    required: boolean | undefined;
  }) => React.ReactNode;
}

export const Field: React.FC<FieldProps> = ({
  label,
  required,
  hint,
  error,
  action,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const uid = useId();
  const id = `f${uid}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  // the error manda: si hay error, es lo que debe leerse, no la ayuda.
  const describedBy = [error ? errorId : hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="label-field">
          {label}
          {required && (
            <>
              <span aria-hidden="true" className="ml-0.5 text-negative">*</span>
              <span className="sr-only"> {t('common.required')}</span>
            </>
          )}
        </label>
        {action}
      </div>

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required: required || undefined,
      })}

      {error ? (
        // `role="alert"` so the failure is announced as soon as it appears, without the
        // user needing to refocus the field to find out.
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-[0.8125rem] text-negative">
          <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-[0.8125rem] text-ink-muted leading-5">
          {hint}
        </p>
      ) : null}
    </div>
  );
};

/* --------------------------------------------------------------------------
   Controles
   ----------------------------------------------------------------------- */

const controlBase =
  'w-full rounded-control border bg-surface text-ink placeholder:text-ink-muted ' +
  'transition-colors duration-[var(--duration-quick)] ' +
  'disabled:bg-surface-sunken disabled:text-ink-muted disabled:cursor-not-allowed ' +
  'aria-[invalid=true]:border-negative';

const controlSize = 'h-10 px-3 text-sm';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cn(controlBase, controlSize, 'border-hairline-strong', className)}
        {...rest}
      />
    );
  },
);

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 3, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(controlBase, 'border-hairline-strong px-3 py-2 text-sm leading-6', className)}
      {...rest}
    />
  );
});

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ className, children, ...rest }, ref) {
  return (
    <select
      ref={ref}
      className={cn(
        controlBase,
        controlSize,
        'border-hairline-strong appearance-none bg-no-repeat pr-9',
        className,
      )}
      // Cheurón dibujado con la tinta del tema: un SVG de color fijo se pierde
      // en modo oscuro, y `appearance-none` Deletes el nativo.
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%235f6b82' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 0.65rem center',
        backgroundSize: '16px',
      }}
      {...rest}
    >
      {children}
    </select>
  );
});

/** Grilla de formulario: una columna en móvil, dos desde `sm`. */
export const FieldGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cn('grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2', className)}>{children}</div>;

/** Ocupa el ancho completo dentro de `FieldGrid` (direcciones, notas). */
export const FieldWide: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cn('sm:col-span-2', className)}>{children}</div>;

export default Field;
